import { useMemo } from "react";
import {
  useListInventoryAuditLog,
  useListUsers,
} from "@workspace/api-client-react";
import type { InventoryAuditLog } from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import { History as HistoryIcon, Plus, Pencil, Trash2, Undo2 } from "lucide-react";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tableName: string;
  recordId: number | null;
  recordLabel?: string;
}

const ACTION_META: Record<string, { color: string; icon: typeof Plus; label: string }> = {
  create:  { color: "bg-emerald-100 text-emerald-700",  icon: Plus,    label: "Created" },
  update:  { color: "bg-blue-100 text-blue-700",        icon: Pencil,  label: "Updated" },
  delete:  { color: "bg-red-100 text-red-700",          icon: Trash2,  label: "Deleted" },
  restore: { color: "bg-amber-100 text-amber-700",      icon: Undo2,   label: "Restored" },
};

function fmtTimestamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.round(day / 30);
  if (month < 12) return `${month}mo ago`;
  return `${Math.round(month / 12)}y ago`;
}

// Render which fields changed in an update entry by diffing before/after JSON.
// Keeps the dialog short and operator-readable instead of dumping raw JSONB.
function diffSummary(before: Record<string, unknown> | null | undefined, after: Record<string, unknown> | null | undefined): string[] {
  if (!before || !after) return [];
  const out: string[] = [];
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  // Skip fields that change on every update or aren't operator-meaningful
  const SKIP = new Set(["updatedAt", "updated_at", "createdAt", "created_at"]);
  for (const k of keys) {
    if (SKIP.has(k)) continue;
    const b = before[k];
    const a = after[k];
    if (JSON.stringify(b) === JSON.stringify(a)) continue;
    const bStr = b === null || b === undefined ? "—" : String(b);
    const aStr = a === null || a === undefined ? "—" : String(a);
    if (bStr.length > 40 || aStr.length > 40) {
      out.push(`${k}: changed`);
    } else {
      out.push(`${k}: ${bStr} → ${aStr}`);
    }
  }
  return out;
}

export function AuditHistoryDialog({ open, onOpenChange, tableName, recordId, recordLabel }: Props) {
  const { data: logsPage, isLoading } = useListInventoryAuditLog(
    {
      tableName,
      recordId: recordId ?? undefined,
      pageSize: 50,
    },
    {
      query: {
        queryKey: ["/api/inventory/audit-log", tableName, recordId ?? 0],
        enabled: open && recordId !== null,
      },
    }
  );
  const logs = logsPage?.items ?? [];

  const { data: users } = useListUsers({
    query: {
      queryKey: ["/api/users"],
      enabled: open,
    },
  });

  const userById = useMemo(() => {
    const map = new Map<number, string>();
    for (const u of users ?? []) map.set(u.id, u.name);
    return map;
  }, [users]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HistoryIcon className="h-4 w-4" />
            Activity history
            {recordLabel && <span className="text-muted-foreground font-normal">· {recordLabel}</span>}
          </DialogTitle>
          <DialogDescription>
            Append-only audit trail. Most recent first.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          {isLoading ? (
            <div className="space-y-3 py-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <Skeleton className="h-6 w-6 rounded-full" />
                  <div className="flex-1 space-y-1">
                    <Skeleton className="h-4 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : !logs || logs.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6">
              No history entries yet.
            </div>
          ) : (
            <ol className="relative border-l-2 border-muted ml-3 space-y-4 py-2">
              {logs.map((entry: InventoryAuditLog) => {
                const meta = ACTION_META[entry.action] ?? ACTION_META["update"]!;
                const Icon = meta.icon;
                const actorName = entry.actorUserId
                  ? userById.get(entry.actorUserId) ?? `User #${entry.actorUserId}`
                  : "System";
                const diffs = entry.action === "update" ? diffSummary(entry.before, entry.after) : [];
                return (
                  <li key={entry.id} className="ml-5">
                    <span className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ring-4 ring-background ${meta.color}`}>
                      <Icon className="h-3 w-3" />
                    </span>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs">{meta.label}</Badge>
                      <span className="text-sm font-medium">{actorName}</span>
                      <span className="text-xs text-muted-foreground">
                        {relativeTime(entry.at)} · {fmtTimestamp(entry.at)}
                      </span>
                    </div>
                    {diffs.length > 0 && (
                      <ul className="mt-1 text-xs text-muted-foreground space-y-0.5">
                        {diffs.slice(0, 6).map((d, i) => (
                          <li key={i} className="font-mono">{d}</li>
                        ))}
                        {diffs.length > 6 && (
                          <li className="italic">…and {diffs.length - 6} more fields</li>
                        )}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ol>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
